import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { EventContentArg } from '@fullcalendar/core';
import { Evento, Festivo } from '../../types/index';
import { Printer, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getEventBackgroundColor } from '../../utils/utils';

interface CompressedCalendarProps {
    eventos: Evento[];
    festivos: Festivo[];
    onClose: () => void;
    viewStartDate: string;
    viewEndDate: string;
}

const CompressedCalendar: React.FC<CompressedCalendarProps> = ({ eventos, festivos, onClose, viewStartDate, viewEndDate }) => {
    
    // Convertir festivos a eventos de FullCalendar
    const festivoEvents = festivos?.map(festivo => ({
        id: festivo._id,
        title: festivo.descripcion || 'Festivo',
        start: festivo.fecha,
        allDay: true,
        display: 'background',
        backgroundColor: '#ff9f89', // Color para los festivos
        borderColor: '#ff9f89',
        textColor: '#000000',
    })) || [];

    // Combinar eventos normales y festivos
    const allEvents = [...(eventos || []), ...festivoEvents];

    // Función para renderizar el contenido de los eventos
    const renderEventContent = (eventInfo: EventContentArg) => {
        // console.log("compress calendar -> ", eventInfo);
        if (eventInfo.event.extendedProps._id) {
            const backgroundColor = getEventBackgroundColor(eventInfo.event.extendedProps.nivel);
            return (
                <div 
                    className="h-full w-full rounded-xl overflow-hidden p-2" 
                    style={{ backgroundColor }}
                >
                    {eventInfo.event.extendedProps.cancelado && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <p className="text-white text-lg font-bold">CANCELADO</p>
                        </div>
                    )}
                    <div className='flex flex-col justify-center items-center h-full gap-1'>
                        <p className="text-xs font-semibold text-white text-center">{eventInfo.event.extendedProps.horaInicio} - {eventInfo.event.title}</p>
                        <p className="text-sm font-bold text-center text-white">{eventInfo.event.extendedProps.sala}</p>
                    </div>
                    
                </div>
            );
        } else {
            return (
                <div className='h-full w-full  rounded-xl overflow-hidden p-2'>
                    <p className='text-black'>FESTIVO - {eventInfo.event.title}</p>
                </div>
            );
        }
    };

    // const handlePrint = () => {
    //     const printContents = document.getElementById('printable-calendar')?.innerHTML;
    //     const originalContents = document.body.innerHTML;

    //     if (printContents) {
    //         document.body.innerHTML = printContents;
    //         window.print();
    //         document.body.innerHTML = originalContents;
    //         window.location.reload(); // Recargar la página para restaurar el contenido original
    //     }
    // };

    const handleExportPDF = async () => {
        const input = document.getElementById('printable-calendar');
        if (!input) return;

        // Primero, asegurarse de que todo el contenido sea visible
        const originalStyle = input.style.maxHeight;
        input.style.maxHeight = 'none';
        
        try {
            const canvas = await html2canvas(input, {
                scale: 2, // Mejor calidad
                useCORS: true,
                logging: false,
                windowHeight: input.scrollHeight,
                height: input.scrollHeight,
                onclone: (document) => {
                    // Asegurarse de que los elementos del calendario sean visibles en el clon
                    const clonedInput = document.getElementById('printable-calendar');
                    if (clonedInput) {
                        clonedInput.style.maxHeight = 'none';
                        clonedInput.style.height = 'auto';
                    }
                }
            });

            const imgData = canvas.toDataURL('image/png');
            
            // Crear PDF en formato A4 apaisado
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            // Calcular las dimensiones para mantener la proporción
            const ratio = canvas.width / canvas.height;
            let imgWidth = pdfWidth;
            let imgHeight = imgWidth / ratio;

            // Si la altura calculada es mayor que la altura de la página,
            // ajustar basándose en la altura
            if (imgHeight > pdfHeight) {
                imgHeight = pdfHeight;
                imgWidth = imgHeight * ratio;
            }

            // Centrar la imagen en la página
            const x = (pdfWidth - imgWidth) / 2;
            const y = (pdfHeight - imgHeight) / 2;

            pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
            pdf.save('calendario.pdf');
        } catch (error) {
            console.error('Error al generar PDF:', error);
        } finally {
            // Restaurar el estilo original
            input.style.maxHeight = originalStyle;
        }
    };

    
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-5xl max-h-[70vh] overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <div className="flex justify-start my-4">
                    <button
                        type="button"
                        onClick={handleExportPDF}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                    >
                        <Printer className="mr-2" />
                            Exporta a PDF
                    </button>
                </div>
                <div id="printable-calendar" className="[&_.fc-event]:bg-transparent [&_.fc-event]:border-transparent [&_.fc-timegrid-slot]:h-12
                                [&_.fc-event]:my-1 
                                [&_.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky>*]:static
                                [&_.fc-event]: w-fit-content
                ">
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        initialDate={viewStartDate}
                        validRange={{ start: viewStartDate, end: viewEndDate }}
                        events={allEvents}
                        locale={esLocale}
                        headerToolbar={{
                            left: '',
                            center: 'title',
                            right: ''
                        }}
                        eventContent={renderEventContent}
                        height="auto"
                        slotMinTime="08:00:00"
                        slotMaxTime="22:00:00"
                        allDaySlot={false}
                        weekends={false}
                        slotEventOverlap={false}
                        eventDisplay="auto"
                        expandRows={true}
                    />
                </div>
                <div className='text-right mt-4'>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                        Cerrar
                    </button>
                </div>
                
            </div>
           
        </div>
    );
};

export default CompressedCalendar;